<template>
  <v-card class="account-creations-table">
    <v-card-title class="toolbar white--text">
      <h2>Account Creations</h2>
      <v-spacer />
      <v-btn
        dark
        color="blue"
        @click.stop="openCreateAccountDialog()"
      >
        <v-icon dark>
          add
        </v-icon>Create
      </v-btn>
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                restore
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="resultFilters.status"

        class="ml-5"
        primary
        :items="status"
        label="Status"
        item-value="value"
        clearable
      />

      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :loading="loading"
      :expand="expand"
      :pagination.sync="pagination"
      item-key="id"
      class="elevation-1"
    >
      <template v-slot:items="props">
        <tr
          :active="props.selected"
          @click="props.expanded = !props.expanded"
        >
          <td class="text-xs-center">
            {{ props.item.user_id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.user_detail.FirstName }} {{ props.item.user_detail.LastName }}
          </td>
          <td class="text-xs-center">
            {{ props.item.bank_data.snr }}
          </td>
          <td class="text-xs-center">
            {{ props.item.status.status }}
          </td>
          <td class="text-xs-center">
            {{ props.item.creation_path }}
          </td>
          <td class="text-xs-center">
            {{ formatDateSmall(props.item.created_at) }}
          </td>
          <td class="text-xs-center">
            {{ formatDateSmall(props.item.updated_at) }}
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <BaaderRowDetail
          :item="props.item"
          :user-id="props.item.user_id"
        />
      </template>
    </v-data-table>

    <create-account
      :dialog="isOpenCreationDialog"
      :submit-action="submitCreateAccount"
      :cancel-action="cancelCreationModal"
      :create-account-error="createError"
    />
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions } from 'vuex';
import AccountCreationStatusEnum from '@/enum/baader/AccountCreationStatus';
import BaaderRowDetail from './shared/BaaderRowDetail';
import CreateAccount from './CreateAccount';

export default {
  name: 'AccountCreationsTable',
  components: {
    BaaderRowDetail,
    'create-account': CreateAccount,
  },
  props: {
    accountCreations: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      isLoadTableData: false,
      selected: [],
      createError: '',
      isOpenCreationDialog: false,
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        status: null,
        searchResults: null,
      },
      headers: [
        {
          text: 'User Id',
          align: 'left',
          value: 'user_id',
        },
        {
          text: 'Name',
          align: 'left',
          value: 'name',
        },
        {
          text: 'Snr',
          align: 'left',
          value: 'snr',
        },
        {
          text: 'Status',
          align: 'left',
          value: 'status',
        },
        {
          text: 'Creation Path',
          align: 'left',
          value: 'creation_path',
        },
        {
          text: 'Created at',
          align: 'left',
          value: 'created_at',
        },
        {
          text: 'Updated at',
          value: 'updated_at',
        },
      ],
      pagination: {
        page: 1,
        rowsPerPage: 5,
        sortBy: 'created_at',
        descending: true,
      },
    };
  },
  computed: {
    filteredItems() {
      return this.accountCreations.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let hasStatus = false;
          let hasSearch = false;

          const { status, searchResults } = this.resultFilters;

          if (status) {
            hasStatus = item.status.status === status;
          }
          if (searchResults) {
            hasSearch = JSON.stringify(item)
              .toLowerCase()
              .includes(searchResults.toLowerCase());
          }

          return (!!hasStatus || !!hasSearch) || false;
        });
      }

      return this.filteredItems;
    },
    status() {
      return Object.keys(AccountCreationStatusEnum).map(key => ({
        text: AccountCreationStatusEnum[key],
        value: AccountCreationStatusEnum[key],
      }));
    },
    hasResultFilters() {
      return !!this.resultFilters.status
        || !!this.resultFilters.searchResults;
    },
  },
  methods: {
    ...mapActions([
      'createBaaderAccount',
    ]),
    formatDateSmall(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },
    openCreateAccountDialog() {
      this.createError = '';
      this.isOpenCreationDialog = true;
    },
    cancelCreationModal() {
      this.isOpenCreationDialog = false;
      this.createError = '';
    },
    async submitCreateAccount(data) {
      try {
        await this.createBaaderAccount(data);
        this.$emit('accountCreated');
        this.isOpenCreationDialog = false;
      } catch (e) {
        this.createError = 'Fehler bei der Accounterstellung';
        this.isOpenCreationDialog = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.account-creations-table{
  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
        flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/baader/AccountCreationsTable.vue